// Menu wiring, the rebuilt end screen (result ticket + stats), the share overlay, and the
// universal play-counter fetch. See web-design-refs/end-screen-layout-spec.md.
import { G, reset } from '../game/state';
import { GAME_URL } from '../game/config';
import { pageHeadline, scoreTitle } from '../game/scoring';
import { resumeAudio } from '../fx/audio';
import { startCamera } from '../input/camera';
import { NO_FALLBACK, padShiftNo, renderResultTicket, statStrings, variantForScore, type TicketData } from './resultTicket';

const $ = (id: string) => document.getElementById(id)!;
const EXPORT_WIDTH = 1400; // 2× the ~700px display width → crisp shared/downloaded ticket

let lastTicket: TicketData | null = null;
let forcedNo: string | null = null; // set by the ?score debug preview so placement is visible

// ----- universal play counter -----
// POST /api/shift once per round end → zero-padded 6-digit count. Dash fallback on any error
// (offline, no backend in dev, bad payload) — never a fake number.
async function fetchShiftNo(): Promise<string> {
  if (forcedNo) return forcedNo;
  try {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 2500);
    const r = await fetch('/api/shift', { method: 'POST', signal: ctrl.signal });
    clearTimeout(timer);
    if (!r.ok) throw new Error('bad status');
    const d: any = await r.json();
    const n = typeof d === 'number' ? d : d.count ?? d.no ?? d.value;
    if (typeof n !== 'number' || !isFinite(n)) throw new Error('bad payload');
    return padShiftNo(n);
  } catch {
    return NO_FALLBACK;
  }
}

// ----- end screen -----
export function showEnd(): void {
  G.state = 'end';
  const [ti, sub] = pageHeadline();
  $('endTitle').textContent = '“' + ti + '”';
  $('endSub').textContent = sub;
  $('resultTicket').innerHTML = '';
  $('end').classList.remove('hidden');
  void buildTicket();
}

// Resolve the play number + render the ticket, then drop it into the page (async so the
// headline shows immediately; the ticket fills in when its art + number are ready).
async function buildTicket(): Promise<void> {
  const variant = variantForScore(G.score);
  const stats = statStrings(G.score, G.resolved, G.breaches, G.bestBatch);
  const no = await fetchShiftNo();
  lastTicket = { variant, no, stats };
  try {
    const canvas = await renderResultTicket(lastTicket);
    const host = $('resultTicket');
    host.innerHTML = '';
    host.appendChild(canvas);
  } catch {
    /* art failed to load — leave the slot empty rather than crash the end screen */
  }
}

// ----- share -----
function shareText(): string {
  const title = scoreTitle()[0];
  return `I earned the title "${title}" in Ticket Ninja, a game by Parahelp.\nCan you beat my shift? → ${GAME_URL}`;
}

// The crisp 2× ticket as a File for Web Share / download.
async function ticketFile(): Promise<File | null> {
  if (!lastTicket) return null;
  try {
    const canvas = await renderResultTicket(lastTicket, EXPORT_WIDTH);
    const blob = await new Promise<Blob | null>((res) => canvas.toBlob(res, 'image/png'));
    return blob ? new File([blob], 'ticket-ninja.png', { type: 'image/png' }) : null;
  } catch {
    return null;
  }
}

// Mobile primary: Web Share with the ticket image attached (image travels with the post).
// Desktop fallback: text/URL-only intent for the chosen platform.
async function shareVia(platform: 'x' | 'linkedin'): Promise<void> {
  const text = shareText();
  const file = await ticketFile();
  if (file && navigator.canShare && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({ files: [file], text });
      return;
    } catch {
      /* user cancelled or share failed → fall through to the intent */
    }
  }
  const url =
    platform === 'x'
      ? 'https://twitter.com/intent/tweet?text=' + encodeURIComponent(text)
      : 'https://www.linkedin.com/sharing/share-offsite/?url=' + encodeURIComponent(GAME_URL);
  window.open(url, '_blank', 'noopener,noreferrer');
}

async function downloadTicket(): Promise<void> {
  const file = await ticketFile();
  if (!file) return;
  const url = URL.createObjectURL(file);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'ticket-ninja.png';
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function wireShare(): void {
  const overlay = $('shareOverlay');
  const open = async () => {
    if (lastTicket) {
      try {
        const c = await renderResultTicket(lastTicket);
        const host = $('shareTicket');
        host.innerHTML = '';
        host.appendChild(c);
      } catch {
        /* ignore */
      }
    }
    overlay.classList.add('open');
  };
  const close = () => overlay.classList.remove('open');
  $('shareBtn').onclick = open;
  $('shareClose').onclick = close;
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) close();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') close();
  });
  $('shareLinkedIn').onclick = () => void shareVia('linkedin');
  $('shareX').onclick = () => void shareVia('x');
  $('shareCopy').onclick = () => {
    navigator.clipboard
      .writeText(shareText())
      .then(() => toast('Copied!'))
      .catch(() => toast(GAME_URL));
  };
  $('shareDownload').onclick = () => void downloadTicket();
}

function toast(msg: string): void {
  const d = document.createElement('div');
  d.className = 'toast';
  d.textContent = msg;
  document.body.appendChild(d);
  setTimeout(() => d.remove(), 2200);
}

// Rules overlay: click "?" to open; close on outside-click, ✕, or Esc (mirrors the mock).
function wireRules(): void {
  const overlay = $('rulesOverlay');
  const open = () => overlay.classList.add('open');
  const close = () => overlay.classList.remove('open');
  $('rulesBtn').onclick = open;
  $('rulesClose').onclick = close;
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) close();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') close();
  });
}

// Debug: ?score=N (+ optional &death=angry|sla &no=NNNNNN) jumps straight to the end screen
// with a forced score so all six ticket variants + both death types can be previewed quickly.
function maybePreview(): boolean {
  const q = new URLSearchParams(location.search);
  if (!q.has('score')) return false;
  G.score = Math.max(0, parseInt(q.get('score') || '0', 10) || 0);
  const death = q.get('death');
  G.deathBy = death === 'angry' ? 'angry' : death === 'sla' ? 'sla' : null;
  // plausible stats so the ticket looks realistic
  G.resolved = Math.max(0, Math.round(G.score / 20));
  G.breaches = death === 'sla' ? 3 : death === 'angry' ? 1 : 0;
  G.bestBatch = G.score >= 100 ? 5 : 0;
  forcedNo = (q.get('no') || '000247').padStart(6, '0').slice(0, 6);
  $('menu').classList.add('hidden');
  showEnd();
  return true;
}

export function wireUI(): void {
  wireRules();
  wireShare();

  $('startBtn').onclick = () => {
    resumeAudio();
    G.camMode = false; // normal (pointer) mode, in case we came back from camera via the menu
    $('menu').classList.add('hidden');
    reset();
  };
  $('camBtn').onclick = () => {
    resumeAudio();
    $('menu').classList.add('hidden');
    void startCamera(); // idempotent: re-enters camwait if the camera is already initialised
  };
  // "One more shift" → back to the MENU (mode selection), not straight into a round.
  $('againBtn').onclick = () => {
    $('end').classList.add('hidden');
    $('shareOverlay').classList.remove('open');
    G.camMode = false;
    G.state = 'menu';
    $('menu').classList.remove('hidden');
  };

  maybePreview();
}
